import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiChevronDown, FiUser, FiLogOut, FiSettings } from 'react-icons/fi';
import * as LuIcons from 'react-icons/lu';
import FiIcon from '../common/FiIcon';
import { NAV_LINKS, SITE_NAME, SITE_FULL_NAME } from '../../utils/constants';
import { cn } from '../../utils/helpers';
import Button from '../common/Button';
import { useSettings } from '../../contexts/SettingsContext';
import { useMemberAuth } from '../../contexts/MemberAuthContext';
import ChatNavButton from '../chat/ChatNavButton';
import { apiService } from '../../services/api';

function NavIcon({ name, className }) {
  if (!name) return null;
  const Icon = LuIcons[name];
  if (!Icon) return null;
  return <Icon className={className} />;
}

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [navLinks, setNavLinks] = useState(NAV_LINKS);
  const headerRef = useRef(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileUserMenu, setShowMobileUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { settings, navigationSettings } = useSettings();
  const { member, isAuthenticated, logout } = useMemberAuth();

  // Fetch navigation items and settings
  useEffect(() => {
    const fetchNavigationData = async () => {
      try {
        const [navResponse] = await Promise.all([
          apiService.getNavigation(),
        ]);
        if (navResponse.data && navResponse.data.length > 0) {
          // Transform database format to component format
          const topLevelItems = navResponse.data
            .filter(item => !item.parent_id)
            .map(item => {
              const children = navResponse.data
                .filter(child => child.parent_id === item.id)
                .map(child => ({
                  label: child.label,
                  href: child.url,
                  icon: child.icon || null,
                }));

              return {
                label: item.label,
                href: item.url,
                icon: item.icon || null,
                children: children.length > 0 ? children : undefined,
              };
            });
          setNavLinks(topLevelItems);
        }

      } catch (error) {
        console.error('Failed to fetch navigation data:', error);
        // Fallback to defaults on error
      }
    };

    fetchNavigationData();
  }, []);
  
  // Close menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    setShowUserMenu(false);
    setShowMobileUserMenu(false);
  }, [location]);

  // Close user menu on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Set --navbar-height CSS variable from actual header height
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--navbar-height', `${el.offsetHeight}px`);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Helper function to convert hex to RGBA
  const hexToRGBA = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Determine text colors based on theme
  const textColor = navigationSettings.text_theme === 'light' ? 'text-white' : 'text-primary';
  const textHoverColor = navigationSettings.text_theme === 'light' ? 'hover:text-white/80' : 'hover:text-accent';

  return (
    <>
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      <header
        ref={headerRef}
        className={cn(
          'fixed left-0 right-0 z-50 transition-all duration-300',
          navigationSettings.background_enabled
            ? 'shadow-sm'
            : isScrolled
              ? (navigationSettings.text_theme === 'light' ? 'bg-primary backdrop-blur-md shadow-sm' : 'bg-white/95 backdrop-blur-md shadow-sm')
              : 'bg-transparent'
        )}
        style={{
          top: 'var(--alert-banner-height, 0px)',
          ...(navigationSettings.background_enabled ? {
            backgroundColor: hexToRGBA(navigationSettings.background_color, navigationSettings.background_opacity)
          } : {}),
        }}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-3 group"
              aria-label="CGS Home"
            >
              {settings.logo_url ? (
                <>
                  <img
                    src={settings.logo_url}
                    alt={settings.site_name || SITE_FULL_NAME}
                    className="rounded-[8px] h-12 w-auto object-contain transition-transform group-hover:scale-105"
                  />
                  <span className={cn("hidden sm:block font-serif text-lg font-medium tracking-tight", textColor)}>
                    {settings.site_name}
                  </span>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 bg-primary rounded flex items-center justify-center transition-transform group-hover:scale-105">
                    <span className="text-accent font-semibold text-lg">{SITE_NAME}</span>
                  </div>
                  <span className={cn("hidden sm:block font-serif text-lg font-medium tracking-tight", textColor)}>
                    {SITE_FULL_NAME}
                  </span>
                </>
              )}
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => link.children && setActiveDropdown(link.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {link.children ? (
                    <>
                      <Link
                        to={link.href}
                        className={cn(
                          'flex items-center leading-none gap-1.5 font-medium text-[15px] py-2 transition-colors relative group',
                          textColor,
                          textHoverColor
                        )}
                      >
                        <NavIcon name={link.icon} className="w-4 h-4 flex-shrink-0" />
                        {link.label}
                        <FiChevronDown className={cn(
                          'w-4 h-4 transition-transform',
                          activeDropdown === link.label && 'rotate-180'
                        )} />
                        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all group-hover:w-full" />
                      </Link>

                      <AnimatePresence>
                        {activeDropdown === link.label && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute top-full left-0 pt-1 min-w-[180px]"
                          >
                            <div className="py-2 bg-white rounded-lg shadow-lg border border-gray-100">
                              {link.children.map((child) => (
                                <Link
                                  key={child.href}
                                  to={child.href}
                                  className={cn(
                                    'flex items-center transition-[margin-left] px-4 py-2 text-sm text-primary/80 hover:text-primary hover:bg-highlight/50 hover:ml-1'
                                  )}
                                >
                                  <NavIcon name={child.icon} className="w-3 h-3 flex-shrink-0 ml-[-5px] mr-1" />
                                  {child.label}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link
                      to={link.href}
                      className={cn(
                        'flex items-center gap-1.5 font-medium text-[15px] py-2 transition-colors relative group',
                        textColor,
                        textHoverColor
                      )}
                    >
                      <NavIcon name={link.icon} className="w-4 h-4 flex-shrink-0" />
                      {link.label}
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all group-hover:w-full" />
                    </Link>
                  )}
                </div>
              ))}

              {/* Conversations — left of the avatar. Renders nothing when
                  signed out. */}
              {navigationSettings.login_enabled && (
                <ChatNavButton className={cn(textColor, textHoverColor)} />
              )}

              {/* Login Icon / User Dropdown */}
              {navigationSettings.login_enabled && (
                <div className="relative" ref={userMenuRef}>
                  {isAuthenticated ? (
                    <>
                      <button
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        className={cn(
                          'flex items-center gap-1.5 p-2 rounded-lg transition-colors',
                          textColor,
                          textHoverColor
                        )}
                        aria-label="User menu"
                        aria-expanded={showUserMenu}
                      >
                        <div className="w-8 h-8 rounded-full bg-accent text-primary flex items-center justify-center text-sm font-medium">
                          {member?.first_name?.charAt(0) || member?.firstName?.charAt(0) || <FiUser className="w-4 h-4" />}
                        </div>
                        <FiChevronDown className={cn(
                          'w-3.5 h-3.5 transition-transform',
                          showUserMenu && 'rotate-180'
                        )} />
                      </button>

                      <AnimatePresence>
                        {showUserMenu && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute right-0 top-full mt-1 py-1 bg-white rounded-lg shadow-lg border border-gray-100 min-w-[200px] z-50"
                          >
                            <div className="px-4 py-2 border-b border-gray-100">
                              <p className="text-sm font-medium text-gray-900">
                                {member?.first_name || member?.firstName}{' '}
                                {member?.last_name || member?.lastName}
                              </p>
                              <p className="text-xs text-gray-500">{member?.email}</p>
                            </div>
                            {(navigationSettings.login_menu_links || []).map((menuLink, idx) => (
                              <Link
                                key={idx}
                                to={menuLink.url}
                                target={menuLink.target || '_self'}
                                rel={menuLink.target === '_blank' ? 'noopener noreferrer' : undefined}
                                onClick={() => setShowUserMenu(false)}
                                className="block px-4 py-2 text-sm text-primary/80 hover:text-primary hover:bg-highlight/50 transition-colors flex items-center gap-2"
                              >
                                <FiIcon name={menuLink.icon} className="w-4 h-4" />
                                {menuLink.title}
                              </Link>
                            ))}
                            {/* Hardcoded rather than added to login_menu_links:
                                Settings is a fixed member route that ships with
                                the site, not something an admin curates. */}
                            <Link
                              to="/member/preferences"
                              onClick={() => setShowUserMenu(false)}
                              className="block px-4 py-2 text-sm text-primary/80 hover:text-primary hover:bg-highlight/50 transition-colors flex items-center gap-2"
                            >
                              <FiSettings className="w-4 h-4" />
                              Settings
                            </Link>
                            <div className="border-t border-gray-100">
                              <button
                                onClick={() => {
                                  logout();
                                  setShowUserMenu(false);
                                  navigate('/');
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                              >
                                <FiLogOut className="w-4 h-4" />
                                Sign Out
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link
                      to={navigationSettings.login_url}
                      className={cn(
                        'p-2 rounded-lg transition-colors',
                        textColor,
                        textHoverColor
                      )}
                      aria-label="Sign in"
                    >
                      <FiUser className="w-5 h-5" />
                    </Link>
                  )}
                </div>
              )}
            </div>
            
            {/* Mobile: Conversations + burger */}
            <div className="lg:hidden flex items-center">
              {navigationSettings.login_enabled && (
                <ChatNavButton className={textColor} size={22} />
              )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={cn("lg:hidden p-2 hover:bg-highlight/50 rounded-lg transition-colors", textColor)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
            </div>
          </div>
        </nav>
        
        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden bg-white border-t border-gray-100 overflow-hidden"
            >
              {/* Cap to the space left below the bar (and alert banner) so a long
                  menu scrolls instead of running off screen. 5rem is the bar's
                  h-20 — not --navbar-height, which measures the whole header and
                  so grows with this menu. The Tailwind vh class is the fallback
                  for browsers without dvh; the inline dvh style wins elsewhere. */}
              <div
                className="px-4 py-4 space-y-1 overflow-y-auto overscroll-contain max-h-[calc(100vh-5rem-var(--alert-banner-height,0px))]"
                style={{ maxHeight: 'calc(100dvh - 5rem - var(--alert-banner-height, 0px))' }}
              >
                {navLinks.map((link) => (
                  <div key={link.label}>
                    {link.children ? (
                      <div className="py-2">
                        <button
                          onClick={() => setActiveDropdown(
                            activeDropdown === link.label ? null : link.label
                          )}
                          className="w-full flex items-center justify-between gap-1.5 text-primary font-medium py-2 hover:text-accent transition-colors"
                          aria-expanded={activeDropdown === link.label}
                        >
                          <span className="flex items-center gap-1.5">
                            <NavIcon name={link.icon} className="w-4 h-4 flex-shrink-0" />
                            {link.label}
                          </span>
                          <FiChevronDown className={cn(
                            'w-5 h-5 transition-transform flex-shrink-0',
                            activeDropdown === link.label && 'rotate-180'
                          )} />
                        </button>

                        <AnimatePresence>
                          {activeDropdown === link.label && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="pl-4 space-y-1 overflow-hidden"
                            >
                              {link.children.map((child) => (
                                <Link
                                  key={child.href}
                                  to={child.href}
                                  className="flex items-center py-2 text-primary/70 hover:text-primary transition-colors"
                                >
                                  <NavIcon name={child.icon} className="w-4 h-4 flex-shrink-0 mr-1" />
                                  {child.label}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        to={link.href}
                        className="flex items-center gap-1.5 py-3 text-primary font-medium hover:text-accent transition-colors"
                      >
                        <NavIcon name={link.icon} className="w-4 h-4 flex-shrink-0" />
                        {link.label}
                      </Link>
                    )}
                  </div>
                ))}
               
                {/* Mobile Login / User Section */}
                {navigationSettings.login_enabled && (
                  <div className="pt-4 border-t border-gray-100">
                    {isAuthenticated ? (
                      <div>
                        {/* Avatar + name act as the dropdown header; the member
                            menu items collapse underneath, matching the nav
                            link accordions above. */}
                        <button
                          onClick={() => setShowMobileUserMenu(!showMobileUserMenu)}
                          className="w-full flex items-center justify-between gap-3 py-2 text-left hover:text-accent transition-colors"
                          aria-label="User menu"
                          aria-expanded={showMobileUserMenu}
                        >
                          <span className="flex items-center gap-3 min-w-0">
                            <span className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                              {member?.first_name?.charAt(0) || member?.firstName?.charAt(0) || 'U'}
                            </span>
                            <span className="min-w-0">
                              <span className="block text-sm font-medium text-gray-900 truncate">
                                {member?.first_name || member?.firstName}{' '}
                                {member?.last_name || member?.lastName}
                              </span>
                              {member?.email && (
                                <span className="block text-xs text-gray-500 truncate">
                                  {member.email}
                                </span>
                              )}
                            </span>
                          </span>
                          <FiChevronDown className={cn(
                            'w-5 h-5 text-primary transition-transform flex-shrink-0',
                            showMobileUserMenu && 'rotate-180'
                          )} />
                        </button>

                        <AnimatePresence>
                          {showMobileUserMenu && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="pl-4 space-y-1 overflow-hidden"
                            >
                              {(navigationSettings.login_menu_links || []).map((menuLink, idx) => (
                                <Link
                                  key={idx}
                                  to={menuLink.url}
                                  target={menuLink.target || '_self'}
                                  rel={menuLink.target === '_blank' ? 'noopener noreferrer' : undefined}
                                  onClick={() => setIsMobileMenuOpen(false)}
                                  className="block py-2 text-primary/70 hover:text-primary transition-colors flex items-center gap-2"
                                >
                                  <FiIcon name={menuLink.icon} className="w-4 h-4" />
                                  {menuLink.title}
                                </Link>
                              ))}
                              <Link
                                to="/member/preferences"
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="block py-2 text-primary/70 hover:text-primary transition-colors flex items-center gap-2"
                              >
                                <FiSettings className="w-4 h-4" />
                                Settings
                              </Link>
                              <button
                                onClick={() => {
                                  logout();
                                  setShowMobileUserMenu(false);
                                  setIsMobileMenuOpen(false);
                                  navigate('/');
                                }}
                                className="flex items-center gap-2 py-2 text-red-600 hover:text-red-700 transition-colors"
                              >
                                <FiLogOut className="w-4 h-4" />
                                Sign Out
                              </button>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        to={navigationSettings.login_url}
                        className="flex items-center gap-2 py-3 text-primary font-medium hover:text-accent transition-colors"
                        
                      >
                        <FiUser className="w-5 h-5" />
                        Sign In
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}
